<template lang="pug">
  div
    .mb-6
      h1.display-1.mb-2 Search Button Component
      p.text--secondary An interactive search button with expandable search field functionality.

    // Component Variations
    .component-section.mb-8#basic-variations
      h3.mb-4.cursor-pointer(@click="navigateToSection('basic-variations')") Basic Variations
      .variations-grid
        .variation-item.mb-6(v-for="variation in searchBtnVariations" :key="variation.name" :id="variation.id")
          .variation-header.mb-3
            h4.cursor-pointer(@click="navigateToSection(variation.id)") {{ variation.name }}
            p.text--secondary.caption {{ variation.description }}

          // Grouped components (side by side)
          template(v-if="variation.isGroup")
            .variation-preview.mb-4.pa-4.border.rounded
              .d-flex.flex-wrap.gap-4.align-center
                div(v-for="button in variation.buttons" :key="button.label" class="d-flex flex-column align-center")
                  yz-search-btn(v-bind="button.props" v-model="button.searchValue" @search="(value) => handleSearchDemo(button.label, value)")
                  small.text--secondary.mt-2(v-if="button.hint") {{ button.hint }}
                  .mt-1.text-caption.text--secondary(v-if="emittedSearchValues[button.label]")
                    strong Emitted:
                    | "{{ emittedSearchValues[button.label] }}"
                  .mt-1.text-caption.text--primary(v-if="variation.id === 'v-model-examples' && button.searchValue")
                    strong V-Model:
                    | "{{ button.searchValue }}"

            .variation-code
              .code-header.d-flex.align-center.mb-2
                span.font-weight-medium Code Examples

              .code-examples
                div(v-for="button in variation.buttons" :key="button.label" class="mb-3")
                  .d-flex.align-center.mb-1
                    yz-btn(icon-only size="md" :icon="copiedCode === button.code ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === button.code ? 'success' : ''" @click="copyCode(button.code)")
                    span.font-weight-medium.ml-2 {{ button.label }}
                  pre.code-block.pa-2.rounded.text-caption.ml-md-8(style="background-color: #f5f5f5; overflow-x: auto;")
                    code {{ button.code }}

          // Individual components
          template(v-else)
            .variation-preview.mb-4.pa-4.border.rounded
              yz-search-btn(v-bind="variation.props" v-model="variation.searchValue" @search="(value) => handleSearchDemo(variation.name, value)")
              .mt-2.text-caption.text--secondary(v-if="emittedSearchValues[variation.name]")
                strong Emitted:
                | "{{ emittedSearchValues[variation.name] }}"
              .mt-1.text-caption.text--primary(v-if="variation.id === 'v-model-examples' && variation.searchValue")
                strong V-Model:
                | "{{ variation.searchValue }}"

            .variation-code
              .code-header.d-flex.align-center.mb-2
                yz-btn(icon-only size="md" :icon="copiedCode === variation.code ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === variation.code ? 'success' : ''" @click="copyCode(variation.code)")
                span.font-weight-medium.ml-2 Code

              pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
                code {{ variation.code }}

    // API Documentation
    .component-section.mb-8#api
      h3.mb-4.cursor-pointer(@click="navigateToSection('api')") API
      .props-table
        v-simple-table
          template(v-slot:default)
            thead
              tr
                th Prop
                th Type
                th Default
                th Description
            tbody
              tr(v-for="prop in searchBtnProps" :key="prop.name")
                td.font-weight-medium.text-no-wrap {{ prop.name }}
                td.text--secondary.text-no-wrap {{ prop.type }}
                td.text--secondary.text-no-wrap {{ prop.default }}
                td.single-line {{ prop.description }}

    // Events Documentation
    .component-section.mb-8#events
      h3.mb-4.cursor-pointer(@click="navigateToSection('events')") Events
      .events-table
        v-simple-table
          template(v-slot:default)
            thead
              tr
                th Event
                th Parameters
                th Description
            tbody
              tr(v-for="event in searchBtnEvents" :key="event.name")
                td.font-weight-medium.text-no-wrap {{ event.name }}
                td.text--secondary.text-no-wrap {{ event.parameters }}
                td.single-line {{ event.description }}
</template>

<script>
export default {
  data() {
    return {
      copiedCode: null,
      searchResults: [],
      // Track emitted search values for demonstration
      emittedSearchValues: {},
      searchBtnVariations: [
        {
          name: 'Basic Search Buttons',
          id: 'basic-search-buttons',
          description: 'Standard search buttons with different sizes',
          isGroup: true,
          buttons: [
            {
              label: 'Small',
              props: {
                size: 'sm'
              },
              searchValue: '',
              code: 'yz-search-btn(size="sm" @search="handleSearch")\n\n// In methods:\nhandleSearch(searchValue) {\n  console.log(\'Search emitted:\', searchValue)\n}',
              hint: 'Small size'
            },
            {
              label: 'Medium',
              props: {
                size: 'md'
              },
              searchValue: '',
              code: 'yz-search-btn(size="md" @search="handleSearch")\n\n// In methods:\nhandleSearch(searchValue) {\n  console.log(\'Search emitted:\', searchValue)\n}',
              hint: 'Medium size'
            },
            {
              label: 'Large',
              props: {
                size: 'lg'
              },
              searchValue: '',
              code: 'yz-search-btn(size="lg" @search="handleSearch")\n\n// In methods:\nhandleSearch(searchValue) {\n  console.log(\'Search emitted:\', searchValue)\n}',
              hint: 'Large size (default)'
            },
            {
              label: 'Extra Large',
              props: {
                size: 'xl'
              },
              searchValue: '',
              code: 'yz-search-btn(size="xl" @search="handleSearch")\n\n// In methods:\nhandleSearch(searchValue) {\n  console.log(\'Search emitted:\', searchValue)\n}',
              hint: 'Extra large size'
            }
          ]
        },
        {
          name: 'Search Button States',
          id: 'search-button-states',
          description: 'Different states and configurations',
          isGroup: true,
          buttons: [
            {
              label: 'Auto Show',
              props: {
                size: 'lg',
                autoShow: true
              },
              searchValue: '',
              code: 'yz-search-btn(size="lg" auto-show @search="handleSearch")',
              hint: 'Field shown by default'
            },
            {
              label: 'Persistent',
              props: {
                size: 'lg',
                persistent: true
              },
              searchValue: '',
              code: 'yz-search-btn(size="lg" persistent @search="handleSearch")',
              hint: 'Field always visible'
            },
            {
              label: 'Disabled',
              props: {
                size: 'lg',
                disabled: true
              },
              searchValue: '',
              code: 'yz-search-btn(size="lg" disabled @search="handleSearch")\n\n// In methods:\nhandleSearch(searchValue) {\n  console.log(\'Search emitted:\', searchValue)\n}',
              hint: 'Disabled state'
            },
            {
              label: 'Not Clearable',
              props: {
                size: 'lg',
                clearable: false
              },
              searchValue: '',
              code: 'yz-search-btn(size="lg" :clearable="false" @search="handleSearch")\n\n// In methods:\nhandleSearch(searchValue) {\n  console.log(\'Search emitted:\', searchValue)\n}',
              hint: 'No clear button'
            }
          ]
        },
        {
          name: 'Custom Styling',
          id: 'custom-styling',
          description: 'Search buttons with custom colors and styling',
          isGroup: true,
          buttons: [
            {
              label: 'Custom Icon Color',
              props: {
                size: 'lg',
                iconColor: 'yz-primary-500'
              },
              searchValue: '',
              code: 'yz-search-btn(size="lg" icon-color="yz-primary-500" @search="handleSearch")',
              hint: 'Primary icon color'
            },
            {
              label: 'Danger Color',
              props: {
                size: 'lg',
                iconColor: 'yz-danger-500'
              },
              searchValue: '',
              code: 'yz-search-btn(size="lg" icon-color="yz-danger-500" @search="handleSearch")',
              hint: 'Danger icon color'
            },
            {
              label: 'Custom Placeholder',
              props: {
                size: 'lg',
                placeholder: 'Search claims...'
              },
              searchValue: '',
              code: 'yz-search-btn(size="lg" placeholder="Search claims..." @search="handleSearch")',
              hint: 'Custom placeholder text'
            },
            {
              label: 'Custom Field Class',
              props: {
                size: 'lg',
                fieldClass: 'custom-search-field'
              },
              searchValue: '',
              code: 'yz-search-btn(size="lg" field-class="custom-search-field" @search="handleSearch")\n\n// In methods:\nhandleSearch(searchValue) {\n  console.log(\'Search emitted:\', searchValue)\n}',
              hint: 'Custom field styling'
            }
          ]
        },
        {
          name: 'V-Model Examples',
          id: 'v-model-examples',
          description: 'Examples using v-model for two-way data binding',
          isGroup: true,
          buttons: [
            {
              label: 'Basic V-Model',
              props: {
                size: 'lg'
              },
              searchValue: '',
              code: 'yz-search-btn(size="lg" v-model="searchQuery" @search="handleSearch")\n\n// In data:\ndata() {\n  return {\n    searchQuery: ""\n  }\n},\n\n// In methods:\nhandleSearch(searchValue) {\n  console.log(\'Search emitted:\', searchValue)\n  console.log(\'Current v-model value:\', this.searchQuery)\n}',
              hint: 'Two-way binding with v-model'
            },
            {
              label: 'Pre-filled V-Model',
              props: {
                size: 'lg'
              },
              searchValue: 'initial search',
              code: 'yz-search-btn(size="lg" v-model="searchQuery" @search="handleSearch")\n\n// In data:\ndata() {\n  return {\n    searchQuery: "initial search"\n  }\n},\n\n// In methods:\nhandleSearch(searchValue) {\n  console.log(\'Search emitted:\', searchValue)\n  console.log(\'Current v-model value:\', this.searchQuery)\n}',
              hint: 'V-model with initial value'
            },
            {
              label: 'Reactive V-Model',
              props: {
                size: 'lg',
                persistent: true
              },
              searchValue: '',
              code: 'yz-search-btn(size="lg" persistent v-model="searchQuery" @search="handleSearch")\n\n// In data:\ndata() {\n  return {\n    searchQuery: ""\n  }\n},\n\n// In computed:\ncomputed: {\n  filteredResults() {\n    if (!this.searchQuery) return this.allResults\n    return this.allResults.filter(item => \n      item.name.toLowerCase().includes(this.searchQuery.toLowerCase())\n    )\n  }\n},\n\n// In methods:\nhandleSearch(searchValue) {\n  console.log(\'Search emitted:\', searchValue)\n}',
              hint: 'Reactive filtering with v-model'
            },
            {
              label: 'Controlled V-Model',
              props: {
                size: 'md',
                placeholder: 'Controlled input...'
              },
              searchValue: '',
              code: 'yz-search-btn(size="md" placeholder="Controlled input..." v-model="searchQuery" @search="handleSearch")\n\n// In data:\ndata() {\n  return {\n    searchQuery: ""\n  }\n},\n\n// In methods:\nhandleSearch(searchValue) {\n  // Programmatically control the value\n  if (searchValue.length > 20) {\n    this.searchQuery = searchValue.substring(0, 20)\n    this.$nextTick(() => {\n      this.$message.warning(\'Search limited to 20 characters\')\n    })\n  }\n}',
              hint: 'Programmatic control of v-model value'
            }
          ]
        },
        {
          name: 'Usage Examples',
          id: 'usage-examples',
          description: 'Common usage patterns and real-world examples with event handling',
          isGroup: true,
          buttons: [
            {
              label: 'Table Search',
              props: {
                size: 'lg',
                placeholder: 'Search table...',
                persistent: true
              },
              searchValue: '',
              code: 'yz-search-btn(size="lg" placeholder="Search table..." persistent @search="filterTable")\n\n// In methods:\nfilterTable(searchQuery) {\n  console.log(\'Filtering table with:\', searchQuery)\n  this.filteredData = this.tableData.filter(row => \n    row.name.toLowerCase().includes(searchQuery.toLowerCase())\n  )\n}',
              hint: 'For data tables with search callback'
            },
            {
              label: 'Patient Search',
              props: {
                size: 'md',
                placeholder: 'Search patients...',
                iconColor: 'yz-blue-500'
              },
              searchValue: '',
              code: 'yz-search-btn(size="md" placeholder="Search patients..." icon-color="yz-blue-500" @search="searchPatients")\n\n// In methods:\nsearchPatients(query) {\n  if (query.length >= 2) {\n    this.patients = this.allPatients.filter(patient => \n      patient.name.toLowerCase().includes(query.toLowerCase()) ||\n      patient.id.includes(query)\n    )\n  }\n}',
              hint: 'For patient search with minimum length'
            },
            {
              label: 'Live Filter (Debounced)',
              props: {
                size: 'sm',
                placeholder: 'Live filter...',
                autoShow: true,
                debounceDelay: 500
              },
              searchValue: '',
              code: 'yz-search-btn(size="sm" placeholder="Live filter..." auto-show :debounce-delay="500" @search="liveFilter")\n\n// In methods:\nliveFilter(query) {\n  console.log(\'Live filtering with:\', query)\n  // This runs after 500ms delay\n  this.filteredItems = query ? \n    this.items.filter(item => item.title.includes(query)) : \n    this.items\n}',
              hint: 'Debounced live filtering'
            },
            {
              label: 'API Search',
              props: {
                size: 'lg',
                placeholder: 'Search API...',
                debounceDelay: 750
              },
              searchValue: '',
              code: 'yz-search-btn(size="lg" placeholder="Search API..." :debounce-delay="750" @search="performAPISearch")\n\n// In methods:\nasync performAPISearch(query) {\n  if (!query.trim()) return\n  \n  this.loading = true\n  try {\n    const response = await this.$api.search(query)\n    this.searchResults = response.data\n    console.log(\'API search results:\', this.searchResults)\n  } finally {\n    this.loading = false\n  }\n}',
              hint: 'API search with loading state'
            }
          ]
        }
      ],
      searchBtnProps: [
        {
          name: 'size',
          type: 'String',
          default: 'lg',
          description: 'Button size. Options: sm, md, lg, xl'
        },
        {
          name: 'icon',
          type: 'String',
          default: '$yzi-search-sm',
          description: 'Icon to display on the search button'
        },
        {
          name: 'iconColor',
          type: 'String',
          default: 'yz-neutral-500',
          description: 'Color of the search icon. Accepts hex colors, YZ variables, or shorthand names'
        },
        {
          name: 'disabled',
          type: 'Boolean',
          default: 'false',
          description: 'Disables the search button and field interactions'
        },
        {
          name: 'searchValue',
          type: 'String',
          default: '',
          description: 'The search query value. Used with v-model for two-way binding'
        },
        {
          name: 'placeholder',
          type: 'String',
          default: 'null',
          description: 'Placeholder text for the search field. Falls back to i18n "type_to_search"'
        },
        {
          name: 'autoShow',
          type: 'Boolean',
          default: 'false',
          description: 'Show the search field immediately when component mounts'
        },
        {
          name: 'persistent',
          type: 'Boolean',
          default: 'false',
          description: 'Keep the search field always visible (no toggle behavior)'
        },
        {
          name: 'clearable',
          type: 'Boolean',
          default: 'true',
          description: 'Show clear button in the search field'
        },
        {
          name: 'autofocus',
          type: 'Boolean',
          default: 'true',
          description: 'Auto-focus the search field when it becomes visible'
        },
        {
          name: 'fieldClass',
          type: 'String',
          default: 'null',
          description: 'Custom CSS class to apply to the search field'
        },
        {
          name: 'fieldStyle',
          type: 'Object',
          default: 'null',
          description: 'Custom inline styles to apply to the search field'
        },
        {
          name: 'debounceDelay',
          type: 'Number',
          default: '750',
          description: 'Delay in milliseconds for debounced search events. Input events are debounced, Enter key triggers immediate search'
        }
      ],
      searchBtnEvents: [
        {
          name: 'input',
          parameters: 'value: String',
          description: 'Emitted when search value changes. Used for v-model binding'
        },
        {
          name: 'search',
          parameters: 'query: String',
          description: 'Emitted when search is triggered (enter key or input change)'
        },
        {
          name: 'clear',
          parameters: 'none',
          description: 'Emitted when search field is cleared'
        },
        {
          name: 'toggle',
          parameters: 'isVisible: Boolean',
          description: 'Emitted when search field visibility changes'
        },
        {
          name: 'focus',
          parameters: 'none',
          description: 'Emitted when search field gains focus'
        },
        {
          name: 'blur',
          parameters: 'none',
          description: 'Emitted when search field loses focus'
        }
      ]
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.scrollToSection()
    })
  },
  watch: {
    '$route.hash': {
      handler() {
        this.scrollToSection()
      },
      immediate: false
    }
  },
  methods: {
    navigateToSection(sectionId) {
      this.$router.push({
        name: this.$route.name,
        hash: `#${sectionId}`,
        query: this.$route.query
      })
    },
    scrollToSection() {
      const sectionIdWithHash = this.$route.hash
      const sectionId = sectionIdWithHash.replace('#', '')
      if (sectionId) {
        this.$nextTick(() => {
          const element = document.getElementById(sectionId)
          if (element) {
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            })
          }
        })
      }
    },
    handleSearchDemo(componentKey, searchValue) {
      // Track the emitted search value for display
      this.$set(this.emittedSearchValues, componentKey, searchValue)
      console.log(`Search emitted from ${componentKey}:`, searchValue)

      // Clear the displayed value after 3 seconds for demo purposes
      setTimeout(() => {
        this.$set(this.emittedSearchValues, componentKey, '')
      }, 3000)
    },
    async copyCode(code) {
      try {
        await navigator.clipboard.writeText(code)
        this.copiedCode = code
        setTimeout(() => {
          this.copiedCode = null
        }, 2000)
      } catch (err) {
        console.error('Failed to copy code:', err)
      }
    }
  }
}
</script>

<style scoped>
.variations-grid {
  max-width: 100%;
}

.variation-item {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 20px;
  background-color: #ffffff;
}

.variation-preview {
  background-color: #fafafa;
  border: 1px dashed #d0d0d0 !important;
}

.code-block {
  background-color: #f8f9fa !important;
  border: 1px solid #e9ecef;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  line-height: 1.5;
}

.code-header {
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 8px;
}

.component-section {
  background-color: #ffffff;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  padding: 24px;
}

.props-table .v-data-table,
.events-table .v-data-table {
  border: 1px solid #e0e0e0;
}

.single-line {
  white-space: normal;
  word-wrap: break-word;
  max-width: 400px;
}

.text-no-wrap {
  white-space: nowrap;
}

.cursor-pointer {
  cursor: pointer;
  transition: color 0.2s ease;
}

.cursor-pointer:hover {
  color: #1976d2;
}

.custom-search-field {
  background-color: #f0f8ff;
  border-radius: 6px;
}
</style>